import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { RAMQInfo, RamqStatus } from "@/Common/Models/RAMQ/RAMQInfo";

class APSSPatientDTO {
    public contactId: string;
    public FormName: string;
    public EncounterId: string;
    public PatientId: string;
    public EmployeeId: string;

    public nom: string;
    public prenom: string;
    public NAM: string;
    public NAMexp: string;
    public NAMjustificationAbsence: string;
    public DDN: string;
    public Sexe: string;
    public noTelUsager1: string;
    public noPosteUsager1: string;
    public noTelUsager2: string;
    public noPosteUsager2: string;
    public nomMere: string;
    public prenomMere: string;
    public adresseLigne1: string;
    public adresseLigne2: string;
    public ville: string;
    public cdPostalIndicateur: string;
    public cdPostal: string;
    public courriel: string;
    public rejoindre: string;
    public nomRef: string;
    public prenomRef: string;
    public noPermisRef: string;
    public noTelRefPS: string;
    public posteTelRef: string;
    public noTelRefFax: string;
    public pointService: string;
    public medecinFamilleIndicateur: string;
    public nomMedecinFamille: string;
    public prenomMedecinFamille: string;
    public nomPSMedecinFamille: string;
    public pointServiceAdresse: string;
    public pointServiceVille: string;
    public pointServiceCP: string;
    public noPermisRef_RAMQ: string;

    public static getAPSSPatientDTO(FormName: string, patientId: string, supervisorContactId: string = null): APSSPatientDTO {
        let patient = patientStore.PatientModels[patientId];

        let patientDTO = new APSSPatientDTO;

        patientDTO.FormName = FormName;
        patientDTO.EncounterId = patientStore.PatientMedicalNoteModels[patientId].get("Encounter").Id;
        patientDTO.EmployeeId = cacheStore.GetCurrentEmployee().ContactFacet_id;

        patientDTO.PatientId = patient.PatientResponse.ContactFacet_id;
        patientDTO.contactId = patient.IndividualResponse.Contact_id;
        patientDTO.nom = patient.IndividualResponse.LastName;
        patientDTO.prenom = patient.IndividualResponse.FirstName;
        patientDTO.NAM = patient.IndividualResponse.NAM;

        if (patient.HealthCards && patient.HealthCards.length > 0 && patient.HealthCards[0].ExpiryDate != null) {
            let dateNamExp = patient.HealthCards[0].ExpiryDate.toString();
            patientDTO.NAMexp = new Date(dateNamExp).toMoment().format("YYMM");
        }

        if (!patient.HealthCards || patient.HealthCards.length == 0)
            patientDTO.NAMjustificationAbsence = "Other";

        if (patient.IndividualResponse.DateOfBirth != null) {
            let dateDDNs = patient.IndividualResponse.DateOfBirth.toString();
            patientDTO.DDN = moment(dateDDNs).format("YYYY-MM-DD");
        }

        patientDTO.Sexe = ["M", "F", "I"][patient.Gender()];

        let phones = patient.GetPhonesByPriority();

        if (phones.length > 0) {
            patientDTO.noTelUsager1 = phones[0].format("{1}-{2}-{3}", "");
            patientDTO.noPosteUsager1 = phones[0].format("", "{Ext}");
        }
        if (phones.length > 1) {
            patientDTO.noTelUsager2 = phones[1].format("{1}-{2}-{3}", "");
            patientDTO.noPosteUsager2 = phones[1].format("", "{Ext}");
        }

        patientDTO.nomMere = patient.IndividualResponse.MotherLastName;
        patientDTO.prenomMere = patient.IndividualResponse.MotherFirstName;

        const addressIndex = Math.max(0, patient.Addresses.findIndex(address => address.IsPreferred));
        patientDTO.adresseLigne1 = patient.GetFormattedAddress(addressIndex, false);
        patientDTO.adresseLigne2 = patient.GetFormattedCity(addressIndex);

        if (patient.Addresses && patient.Addresses.length > 0) {
            let codeLK = cacheStore.GetLookupById(patient.Addresses[addressIndex].StateLookupId);

            if (codeLK && codeLK.LookupCode == "CanadaState_QC") {
                if (!IsNullOrWhiteSpace(patient.Addresses[addressIndex].ZipCode))
                    patientDTO.cdPostal = patient.Addresses[addressIndex].ZipCode.replaceAll(" ", "");
            }
            else
                patientDTO.cdPostalIndicateur = "Résidant à l'extérieur du Québec";
        }
        else
            patientDTO.cdPostalIndicateur = "Inconnu";

        patientDTO.rejoindre = "Par téléphone";
        if (patient.Emails != null) {
            let email = patient.Emails.firstOrDefault(x => x.IsOkToContact && !IsNullOrWhiteSpace(x.Address));
            if (email != null) {
                patientDTO.courriel = email.Address;
                patientDTO.rejoindre = "Par courriel";
            }
        }

        let activeSite = sessionStore.getCurrentEmployee().getActiveSite();
        if (activeSite.Model.Phones && activeSite.Model.Phones.length) {
            patientDTO.noTelRefPS = cacheStore.GetPhoneByType(activeSite.Model.Phones, "PHONE", null, true);
            patientDTO.posteTelRef = "";

            patientDTO.noTelRefFax = cacheStore.GetPhoneByType(activeSite.Model.Phones, "FAX", null, true);
        }
        patientDTO.pointService = activeSite.Model.ClinicalName;

        if (activeSite.Model.Adresses && activeSite.Model.Adresses.length > 0) {
            let address = activeSite.Model.Adresses[0];
            patientDTO.pointServiceAdresse = address.StreetAddress;
            patientDTO.pointServiceVille = address.City;
            patientDTO.pointServiceCP = address.ZipCode;
        }

        const currentEmp = sessionStore.getCurrentEmployee();
        const rule = currentEmp.getIssuingOrgRule();
        const title = rule?.Title;

        let isValidReferenceEmp =
            !EmployeeTitleHelper.isStudent(title)
            && title !== EmployeeTitle.MedicalOfficeAssistant
            && title !== EmployeeTitle.NONE
            && title !== EmployeeTitle.Unknown;

        // Aux Nurses can prescribe labs except for Aux. Nurses should be prescribe
        if (FormName == "05REG Analyses générales en laboratoire") {
            isValidReferenceEmp =
                isValidReferenceEmp
                || (
                    EmployeeTitleHelper.isNurse(rule?.Title)
                    && !(
                        rule?.Title === EmployeeTitle.LicensedPracticalNurse
                        && rule.OrganisationCode === "OIIAQ"
                        && rule.CountryCode === "CA"
                        && rule.StateCode === "QC"
                    )
                );
        }

        if (isValidReferenceEmp) {
            patientDTO.nomRef = currentEmp.Model.LastName;
            patientDTO.prenomRef = currentEmp.Model.FirstName;
            patientDTO.noPermisRef = Employee.getCleanLicenseNumber(currentEmp.Model);
            patientDTO.noPermisRef_RAMQ = RAMQInfo.ResolveRamqNumber(currentEmp.Model);
        }
        else if (!IsNullOrWhiteSpace(supervisorContactId)) {
            let employee = cacheStore.GetEmployeeByContactIdOrDefault(supervisorContactId);
            patientDTO.nomRef = employee.LastName;
            patientDTO.prenomRef = employee.FirstName;
            patientDTO.noPermisRef = Employee.getCleanLicenseNumber(employee);
            patientDTO.noPermisRef_RAMQ = RAMQInfo.ResolveRamqNumber(employee);

            isValidReferenceEmp = true;
        }

        if (patient.HasRamqResponse) {
            const ramq = patient.RAMQInfo;
            const licenceFamilyDoctor = ramq.Extract5DigitLicenceNumber();

            const familyDoctorInGmf = RAMQInfo.activeProfessional(ramq.RAMQInscription?.DoctorClass, licenceFamilyDoctor);

            if (!isValidReferenceEmp && familyDoctorInGmf != null) {
                patientDTO.nomRef = familyDoctorInGmf.EmployeeModel.LastName;
                patientDTO.prenomRef = familyDoctorInGmf.EmployeeModel.FirstName;
                patientDTO.noPermisRef = Employee.getCleanLicenseNumber(familyDoctorInGmf.EmployeeModel);
                patientDTO.noPermisRef_RAMQ = RAMQInfo.ResolveRamqNumber(familyDoctorInGmf.EmployeeModel);
            }

            if (patient.RAMQInfo.Result == RamqStatus.NotSubscribed)
                patientDTO.medecinFamilleIndicateur = "Patient with no family physician";
            else if (familyDoctorInGmf != null && patientDTO.noPermisRef == familyDoctorInGmf.EmployeeModel.FirstLicenceNumber)
                patientDTO.medecinFamilleIndicateur = "Same as referring physician";
            else if (familyDoctorInGmf != null) { // Resolve from Botin CMQ
                patientDTO.nomMedecinFamille = familyDoctorInGmf.EmployeeModel.LastName;
                patientDTO.prenomMedecinFamille = familyDoctorInGmf.EmployeeModel.FirstName;
                patientDTO.nomPSMedecinFamille = activeSite.Model.ClinicalName;
            }
        }

        if (patientDTO.nomMedecinFamille == null || patientDTO.nomMedecinFamille == "") { // Resolve from Myle Assigned Clinicians Information
            patientDTO.nomMedecinFamille = "";
            patientDTO.prenomMedecinFamille = "";
            patientDTO.nomPSMedecinFamille = "";
        }

        return patientDTO;
    }
}

export { APSSPatientDTO };
